/**
 * Garde-fou éditorial rendu visible pendant la saisie.
 *
 * Les règles affichées ici sont exactement celles qui font échouer la
 * publication (`evaluerQualite` dans `@delsa/shared`, appliqué par
 * `landing/tests/qualite-editoriale.test.ts`). Elles arrivent telles quelles
 * de `GET /api/contenu/qualite` : ni les seuils, ni les messages, ni la
 * détection de doublons ne sont recalculés dans le navigateur. Découvrir un
 * refus de publication après vingt minutes de rédaction est une expérience
 * détestable ; la prévenir suppose d'annoncer exactement ce que la publication
 * contrôlera, jamais une approximation locale plus indulgente.
 *
 * **Une seule mesure est faite ici, et il faut savoir pourquoi.** Le rapport du
 * serveur porte sur la dernière version *enregistrée* : il ne peut rien dire du
 * paragraphe en cours de frappe. Le compteur de mots ci-dessous est donc
 * calculé dans le navigateur, à partir du brouillon, puis **recalé sur le
 * rapport** : on n'affiche pas le nombre brut, mais le nombre du serveur
 * corrigé de l'écart entre brouillon et version enregistrée. Deux conséquences
 * voulues :
 *
 * - les champs que l'admin ne connaît pas restent comptés. La réponse directe
 *   d'une commune, par exemple, ne vit pas en base : la landing la lit dans
 *   `COMMUNES_CONTENU`, et le serveur l'inclut dans son total. Compter
 *   naïvement le seul brouillon sous-estimerait la page et afficherait une
 *   alerte imméritée.
 * - dès que l'écart est nul — juste après un enregistrement — le compteur
 *   affiche exactement le chiffre du serveur, sans dérive possible.
 *
 * Tout le reste (FAQ insuffisante, réponse directe absente ou dupliquée) est
 * strictement ce que le serveur a répondu.
 */
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import LinearProgress from "@mui/material/LinearProgress";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { type RapportPage, useQualite } from "./ContenuDonnees";

// ---------------------------------------------------------------------------
// Mesure locale du brouillon
// ---------------------------------------------------------------------------

/**
 * Texte nu d'un fragment HTML. Les balises deviennent une espace — jamais rien —
 * sans quoi « <li>un</li><li>deux</li> » ne compterait que pour un mot.
 *
 * Même règle que `texteDepuisHtml` côté serveur. Le module partagé ne peut pas
 * être importé ici : son assainisseur s'appuie sur `HTMLRewriter`, une API de
 * Bun absente du navigateur, et l'importer ferait entrer tout le module dans le
 * paquet de l'admin. Seul le comptage est donc repris, et il ne sert qu'à
 * afficher un écart, jamais à décider si la page est conforme — cette décision
 * reste au serveur, qui la rendra à l'enregistrement suivant.
 */
function texteNu(html: string): string {
  return html
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim();
}

/** Un mot est un fragment séparé par des espaces contenant une lettre ou un chiffre. */
export function compterMotsHtml(...fragments: string[]): number {
  return texteNu(fragments.join(" "))
    .split(/\s+/)
    .filter((fragment) => /[\p{L}\p{N}]/u.test(fragment)).length;
}

// ---------------------------------------------------------------------------
// Alertes d'une page
// ---------------------------------------------------------------------------

type ProprietesAlertes = {
  /**
   * Chemin public de la page, tel que le rapport le nomme
   * (« /peinture/ », « /renovation-interieure-l-isle-adam/ »).
   * `null` tant que la fiche n'a jamais été enregistrée.
   */
  chemin: string | null;
  type: "prestation" | "commune";
  /** Mots mesurés dans les champs rédactionnels du brouillon. */
  motsBrouillon: number;
  /** Mots mesurés dans les **mêmes** champs, version enregistrée. */
  motsEnregistres: number;
};

export function AlertesQualite({
  chemin,
  type,
  motsBrouillon,
  motsEnregistres,
}: ProprietesAlertes) {
  const qualite = useQualite();

  if (qualite.isPending) return <Skeleton variant="rounded" height={120} />;

  if (qualite.isError) {
    return (
      <Alert severity="warning">
        Les contrôles de qualité éditoriale sont indisponibles ({qualite.error.messageGlobal}). La
        publication, elle, les appliquera quand même.
      </Alert>
    );
  }

  const seuil =
    type === "prestation"
      ? qualite.data.seuils.motsMinPrestation
      : qualite.data.seuils.motsMinCommune;

  const rapport = chemin === null ? undefined : qualite.data.pages.find((p) => p.chemin === chemin);

  // Avant le premier enregistrement, la page n'existe pas encore pour le
  // serveur : seul le compteur de mots est parlant, et il ne porte que sur ce
  // qui est saisi ici.
  if (!rapport) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>
          Qualité éditoriale
        </Typography>
        <JaugeMots mots={motsBrouillon} seuil={seuil} />
        <Typography variant="caption" color="text.secondary">
          Les contrôles complets (questions fréquentes, réponse directe, doublons entre pages)
          s'appliqueront dès le premier enregistrement.
        </Typography>
      </Paper>
    );
  }

  // Recalage : on part du total du serveur et on y reporte l'écart introduit
  // par la saisie en cours. Voir l'en-tête du fichier.
  const estimation = Math.max(0, rapport.mots + motsBrouillon - motsEnregistres);
  const brouillonModifie = motsBrouillon !== motsEnregistres;
  const gravite = rapport.publiee ? "error" : "warning";

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={{ alignItems: "center", justifyContent: "space-between", mb: 1 }}
      >
        <Typography variant="subtitle2">Qualité éditoriale</Typography>
        <Chip
          size="small"
          label={rapport.publiee ? "Page publiée" : "Brouillon"}
          color={rapport.publiee ? "primary" : "default"}
          variant={rapport.publiee ? "filled" : "outlined"}
        />
      </Stack>

      <JaugeMots mots={estimation} seuil={seuil} enCoursDeSaisie={brouillonModifie} />

      {type === "prestation" && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {rapport.nombreFaq} question{rapport.nombreFaq > 1 ? "s" : ""} fréquente
          {rapport.nombreFaq > 1 ? "s" : ""} publiée{rapport.nombreFaq > 1 ? "s" : ""} sur{" "}
          {qualite.data.seuils.faqMin} attendues.
        </Typography>
      )}

      <Stack spacing={1}>
        {rapport.manquements.length === 0 ? (
          <Alert severity="success" icon={<CheckCircleOutlinedIcon />}>
            Aucun manquement à la dernière version enregistrée.
          </Alert>
        ) : (
          rapport.manquements.map((manquement) => {
            // Un défaut de longueur déjà comblé dans le brouillon reste affiché,
            // mais cesse d'être présenté comme bloquant : le dire autrement
            // reviendrait à réclamer un texte qui est là, sous les yeux.
            const combleDansLeBrouillon =
              manquement.code === "contenu_trop_court" && estimation >= seuil;

            return (
              <Alert
                key={manquement.code + manquement.message}
                severity={combleDansLeBrouillon ? "info" : gravite}
              >
                {combleDansLeBrouillon && <AlertTitle>Réglé — reste à enregistrer</AlertTitle>}
                {manquement.message}
              </Alert>
            );
          })
        )}
      </Stack>

      {rapport.manquements.length > 0 && !rapport.publiee && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Cette page est un brouillon : en l'état, elle ne bloque pas la publication du site. Elle
          la bloquera dès qu'elle sera publiée sans être corrigée.
        </Typography>
      )}

      {brouillonModifie && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Le compteur de mots tient compte de votre saisie en cours ; les autres contrôles portent
          sur la dernière version enregistrée et se rafraîchiront à l'enregistrement.
        </Typography>
      )}
    </Paper>
  );
}

function JaugeMots({
  mots,
  seuil,
  enCoursDeSaisie = false,
}: {
  mots: number;
  seuil: number;
  enCoursDeSaisie?: boolean;
}) {
  const proportion = Math.min(100, Math.round((mots / seuil) * 100));
  const atteint = mots >= seuil;

  return (
    <Box sx={{ mb: 1.5 }}>
      <Stack direction="row" sx={{ justifyContent: "space-between", alignItems: "baseline" }}>
        <Typography variant="body2" color={atteint ? "success.main" : "text.secondary"}>
          {mots} mot{mots > 1 ? "s" : ""} sur {seuil} attendus
          {enCoursDeSaisie ? " (saisie en cours)" : ""}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {proportion} %
        </Typography>
      </Stack>
      <LinearProgress
        variant="determinate"
        value={proportion}
        color={atteint ? "success" : "warning"}
        aria-label={`Longueur du contenu : ${mots} mots sur ${seuil} attendus`}
        sx={{ height: 8, borderRadius: 4, mt: 0.5 }}
      />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Vue d'ensemble
// ---------------------------------------------------------------------------

/**
 * Bandeau récapitulatif, à poser en tête des écrans de contenu : il dit d'un
 * coup d'œil si la publication passera, et nomme les pages qui la bloquent.
 */
export function ResumeQualite({ filtre }: { filtre?: RapportPage["type"] }) {
  const qualite = useQualite();

  if (qualite.isPending || qualite.isError) return null;

  const pages = qualite.data.pages.filter(
    (page) => (filtre === undefined || page.type === filtre) && page.manquements.length > 0,
  );
  const bloquantes = pages.filter((page) => page.publiee);

  if (pages.length === 0) {
    return (
      <Alert severity="success">
        Aucune page en défaut : les contrôles de qualité éditoriale passent.
      </Alert>
    );
  }

  return (
    <Alert severity={bloquantes.length > 0 ? "error" : "warning"}>
      <AlertTitle>
        {bloquantes.length > 0
          ? `${bloquantes.length} page${bloquantes.length > 1 ? "s" : ""} publiée${bloquantes.length > 1 ? "s" : ""} bloquera${bloquantes.length > 1 ? "ient" : ""} la publication`
          : `${pages.length} brouillon${pages.length > 1 ? "s" : ""} en défaut`}
      </AlertTitle>
      <Stack component="ul" spacing={0.5} sx={{ pl: 2, m: 0 }}>
        {pages.map((page) => (
          <Typography component="li" variant="body2" key={page.chemin}>
            <strong>{page.titre}</strong> ({page.chemin}) —{" "}
            {page.manquements.map((manquement) => manquement.message).join(" ")}
          </Typography>
        ))}
      </Stack>
    </Alert>
  );
}
